/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

export interface PhoneCountryCode {
    dialingCode: string;
    countryCode: string;
    countryName: string;
}

/**
 * Get the most common international dialing codes for checkout phone input.
 *
 * @param currentCountryCode - Optional ISO 3166-1 alpha-2 country code (e.g. 'GB').
 * @returns Array of phone country codes with their dialing codes and country names
 *   When currentCountryCode provided and it matches an entry's `countryCode`
 *   that entry is moved to the front of the returned array. When omitted,
 *   or when it doesn't match any entry, the array is returned in its default order.
 */
export function getCommonPhoneCountryCodes(currentCountryCode?: string): PhoneCountryCode[] {
    const codes: PhoneCountryCode[] = [
        { dialingCode: '+1', countryCode: 'US', countryName: 'United States' },
        { dialingCode: '+44', countryCode: 'GB', countryName: 'United Kingdom' },
    ];

    if (!currentCountryCode) return codes;

    const index = codes.findIndex((c) => c.countryCode === currentCountryCode.toUpperCase());
    if (index <= 0) return codes;

    return [codes[index], ...codes.filter((_, i) => i !== index)];
}
